import type { Ctx } from 'boardgame.io';
import type { AoSGameState, BuildSpecialLinkArgs } from '../../types';

export interface ValidationResult {
  success: boolean;
  error?: string;
  message?: string;
}

/**
 * 特殊リンク（フェリー/隣接）敷設バリデーション
 *
 * @param G - ゲーム状態
 * @param ctx - Boardgame.io コンテキスト
 * @param args - 敷設引数
 * @returns バリデーション結果
 */
export function validateBuildSpecialLink(
  G: AoSGameState,
  ctx: Ctx,
  args: BuildSpecialLinkArgs
): ValidationResult {
  const player = G.players[ctx.currentPlayer];

  // 1. 倒産チェック
  if (player.isBankrupt) {
    return {
      success: false,
      error: 'PLAYER_BANKRUPT',
      message: 'Bankrupt players cannot build'
    };
  }

  // 2. リンク取得
  let link;
  if (args.linkType === 'ferry') {
    link = G.ferryLinks.find(l => l.id === args.linkId);
  } else if (args.linkType === 'adjacent') {
    link = G.adjacentLinks.find(l => l.id === args.linkId);
  } else {
    return {
      success: false,
      error: 'INVALID_LINK_TYPE',
      message: `Invalid link type: ${args.linkType}`
    };
  }

  // 3. リンク存在チェック
  if (!link) {
    return {
      success: false,
      error: 'LINK_NOT_FOUND',
      message: `Link not found: ${args.linkId}`
    };
  }

  // 4. 所有済みチェック
  if (link.owner !== null) {
    return {
      success: false,
      error: 'LINK_ALREADY_OWNED',
      message: `Link ${args.linkId} is already owned by player ${link.owner}`
    };
  }

  // 5. 資金チェック: コスト支払いは passBuildTrack で一括払い（エンジニアボーナス対応）
  // 個別のコスト不足チェックは削除
  // UI側で累積コストを表示し、所持金オーバー時は Finish ボタンを無効化する

  // 6. 配置上限チェック（エンジニアアクション選択時は4枚まで、それ以外は3枚まで）
  const maxTiles = player.selectedAction === 'ENGINEER' ? 4 : 3;
  if (player.tilesPlacedThisTurn >= maxTiles) {
    return {
      success: false,
      error: 'MAX_TILES_REACHED',
      message: `Maximum tiles placed this turn: ${maxTiles}`
    };
  }

  // 7. フェリーリンク固有チェック：同じヘクスペア間に複数リンクがある場合、1つしか敷設できない
  if (args.linkType === 'ferry') {
    // 同じヘクスペアの他のリンクを検索
    const sameHexPairLinks = G.ferryLinks.filter(l => {
      if (l.id === args.linkId) return false;
      // from/to が同じヘクスペアかチェック
      return (
        (l.from.q === link.from.q && l.from.r === link.from.r &&
         l.to.q === link.to.q && l.to.r === link.to.r) ||
        (l.from.q === link.to.q && l.from.r === link.to.r &&
         l.to.q === link.from.q && l.to.r === link.from.r)
      );
    });

    // 同じヘクスペアのリンクのうち、既にプレイヤーが所有しているものがあるか
    const alreadyOwnedInPair = sameHexPairLinks.some(l => l.owner === ctx.currentPlayer);
    if (alreadyOwnedInPair) {
      return {
        success: false,
        error: 'DUPLICATE_FERRY_LINK',
        message: 'Cannot build multiple ferry links between the same hex pair'
      };
    }
  }

  return { success: true };
}
